import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useEffect, useState } from 'react';
import { Keyboard, Platform, useWindowDimensions } from 'react-native';

import {
  MAX_COMPOSER_HEIGHT,
  MIN_COMPOSER_HEIGHT,
  MIN_IOS_COMPOSER_HEIGHT,
  MIN_NATIVE_COMPOSER_HEIGHT,
  nativeComposerMaxHeight,
} from './composerHeights';

import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';

/**
 * Tracks soft-keyboard visibility on native platforms and resolves the active
 * composer min/max heights based on platform, device size, and keyboard state.
 *
 * Web is a no-op for keyboard tracking and uses the web min/max constants.
 * Native grows to a share of the window, a smaller one on iOS while the
 * keyboard is up so the composer doesn't push itself off-screen.
 */
export function useComposerState() {
  const isCompactLayout = useIsCompactLayout();

  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  const { height: windowHeight } = useWindowDimensions();

  useEffect(() => {
    if (isWeb) {
      return;
    }

    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';

    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showSub = Keyboard.addListener(showEvent, () => setIsKeyboardVisible(true));

    const hideSub = Keyboard.addListener(hideEvent, () => setIsKeyboardVisible(false));

    return () => {
      showSub.remove();

      hideSub.remove();
    };
  }, []);

  const minComposerHeight = isWeb ? MIN_COMPOSER_HEIGHT : MIN_NATIVE_COMPOSER_HEIGHT;

  const isIOSNative = Platform.OS === 'ios' && !isWeb;

  const resolvedMinComposerHeight = isIOSNative ? MIN_IOS_COMPOSER_HEIGHT : minComposerHeight;

  const maxComposerHeight = isWeb
    ? MAX_COMPOSER_HEIGHT
    : nativeComposerMaxHeight({
        windowHeight,
        isKeyboardVisible,
        isCompactLayout,
        isIOS: isIOSNative,
      });

  return {
    isKeyboardVisible,
    minComposerHeight: resolvedMinComposerHeight,
    maxComposerHeight,
  };
}
